import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, XCircle, Download } from "lucide-react";
import { api, euro, API } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { metaPurchase } from "@/lib/pixel";

const GutscheinSuccess = () => {
  const { lang } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const order = params.get("order");
  const [state, setState] = useState("checking");
  const [voucher, setVoucher] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId && !order) { setState("error"); return; }
    let stop = false;
    const poll = async () => {
      try {
        const { data } = order
          ? await api.post(`/vouchers/paypal/capture/${order}`)
          : await api.get(`/vouchers/status/${sessionId}`);
        if (stop) return;
        if (data.status === "active" || data.status === "redeemed") {
          setVoucher(data); setState("paid");
          metaPurchase({ value: data.amount, currency: "EUR", bookingNumber: data.code || data.public_token, contentName: "Gutschein", contentType: "voucher" });
          return;
        }
      } catch { /* retry */ }
      tries.current += 1;
      if (tries.current > 10) { setState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, order]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 pt-28 pb-32" data-testid="gutschein-success-page">
      <div className="max-w-lg w-full text-center">
        {state === "checking" && (
          <div data-testid="gc-checking">
            <Loader2 size={44} className="text-[#ff0033] animate-spin mx-auto" />
            <p className="text-neutral-300 mt-6 font-mono-lab uppercase tracking-widest text-sm">{t("Zahlung wird bestätigt…", "Confirming payment…")}</p>
          </div>
        )}
        {state === "paid" && (
          <div data-testid="gc-confirmed">
            <div className="w-16 h-16 rounded-full bg-[#ff0033] flex items-center justify-center mx-auto mb-5"><Check size={32} className="text-white" /></div>
            <h1 className="font-heading text-4xl md:text-5xl uppercase text-white tracking-tight">{t("Gutschein gekauft!", "Voucher purchased!")}</h1>
            <p className="text-neutral-400 mt-4">{t("Gutschein-Code", "Voucher code")}: <span className="text-[#ff0033] font-mono-lab">{voucher?.code}</span></p>
            <p className="text-white font-heading text-2xl mt-2">{euro(voucher?.amount)}</p>
            <p className="text-neutral-500 text-sm mt-4">{t("Der Gutschein wurde als PDF an", "The voucher PDF was sent to")} {voucher?.email} {t("gesendet.", "")}</p>
            {voucher?.public_token && (
              <a href={`${API}/vouchers/${voucher.public_token}/pdf`} target="_blank" rel="noreferrer" data-testid="gc-download-pdf" className="inline-flex items-center gap-2 mt-6 border border-white/20 text-white font-heading uppercase tracking-wider px-6 py-3 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors">
                <Download size={16} /> {t("Gutschein-PDF öffnen", "Open voucher PDF")}
              </a>
            )}
            <div><Link to="/" className="inline-block mt-6 bg-[#ff0033] text-white font-heading uppercase tracking-wider px-8 py-3 hover:bg-[#cc0029] transition-colors">{t("Zur Startseite", "Back home")}</Link></div>
          </div>
        )}
        {(state === "timeout" || state === "error") && (
          <div data-testid="gc-pending">
            <XCircle size={44} className="text-neutral-500 mx-auto" />
            <h1 className="font-heading text-3xl uppercase text-white tracking-tight mt-5">{t("Zahlung wird verarbeitet", "Payment processing")}</h1>
            <p className="text-neutral-400 mt-4">{t("Deine Zahlung wird noch verarbeitet. Du erhältst den Gutschein per E-Mail, sobald sie abgeschlossen ist.", "Your payment is still processing. You'll receive the voucher by email once complete.")}</p>
            <Link to="/" className="inline-block mt-8 border border-white/20 text-white font-heading uppercase tracking-wider px-8 py-3 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors">{t("Zur Startseite", "Back home")}</Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default GutscheinSuccess;
